import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, BookOpen, Loader2 } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const BotKnowledgeModal = ({ open, onClose, knowledge, whatsappId, onSave }) => {
  const initialState = {
    title: "",
    content: "",
    sourceType: "text",
  };

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (knowledge) {
      setForm({
        title: knowledge.title || "",
        content: knowledge.content || "",
        sourceType: knowledge.sourceType || "text",
      });
    } else {
      setForm(initialState);
    }
  }, [knowledge, open]);

  const handleClose = () => {
    setForm(initialState);
    onClose();
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.warn("Ingresa un título");
      return;
    }
    if (!form.content.trim()) {
      toast.warn("Ingresa el contenido");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, whatsappId };
      if (knowledge) {
        await api.put(`/bot/knowledge/${knowledge.id}`, payload);
        toast.success("Conocimiento actualizado");
      } else {
        await api.post("/bot/knowledge", payload);
        toast.success("Conocimiento agregado");
      }
      if (onSave) onSave();
      handleClose();
    } catch (err) { toastError(err); }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-green-600" />
            {knowledge ? "Editar Conocimiento" : "Agregar Conocimiento"}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Título</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              autoFocus
              placeholder="Ej: Horarios de atención, Catálogo de productos"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Contenido</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
              rows={10}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              placeholder="Pega aquí la información que la IA debe conocer. Cuanto más detallado, mejor responderá."
            />
            <p className="text-xs text-slate-400 mt-1">{form.content.length} caracteres</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button type="button" onClick={handleClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {knowledge ? "Guardar" : "Agregar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BotKnowledgeModal;
